import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CUISINE_TYPES, type CuisineTag } from '@/types';

interface CuisineTagSelectorProps {
  value: CuisineTag[];
  onChange: (cuisineTags: CuisineTag[]) => void;
  className?: string;
  onFieldFocus?: () => void;
}

/**
 * CuisineTagSelector - Regional cuisine tag selection for Indian restaurants
 * Supports multi-selection with regional sub-categories
 */
export const CuisineTagSelector: React.FC<CuisineTagSelectorProps> = ({
  value,
  onChange,
  className,
  onFieldFocus,
}) => {
  const handleCuisineToggle = (cuisineType: keyof typeof CUISINE_TYPES) => {
    const existingTag = value.find(tag => tag.type === cuisineType);
    
    if (existingTag) {
      // Remove the cuisine tag
      const updatedTags = value.filter(tag => tag.type !== cuisineType);
      onChange(updatedTags);
    } else {
      // Add the cuisine tag
      const newTag: CuisineTag = { type: cuisineType };
      onChange([...value, newTag]);
    }
    onFieldFocus?.();
  };

  const handleRegionSelect = (cuisineType: keyof typeof CUISINE_TYPES, region: string) => {
    const updatedTags = value.map(tag => 
      tag.type === cuisineType ? { ...tag, region } : tag
    );
    onChange(updatedTags);
    onFieldFocus?.();
  };

  const handleSubTypeSelect = (cuisineType: keyof typeof CUISINE_TYPES, subType: string) => {
    const updatedTags = value.map(tag => 
      tag.type === cuisineType ? { ...tag, subType } : tag
    );
    onChange(updatedTags);
    onFieldFocus?.();
  };

  const isCuisineSelected = (cuisineType: keyof typeof CUISINE_TYPES) => {
    return value.some(tag => tag.type === cuisineType);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        {/* Main cuisine types */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(CUISINE_TYPES).map(([key, cuisineData]) => (
            <motion.button
              key={key}
              type="button"
              onClick={() => handleCuisineToggle(key as keyof typeof CUISINE_TYPES)}
              onFocus={onFieldFocus}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "relative p-3 rounded-lg border-2 transition-all duration-200",
                "flex flex-col items-center text-center space-y-2",
                "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2",
                isCuisineSelected(key as keyof typeof CUISINE_TYPES)
                  ? "border-primary bg-primary/10 text-gray-900 focus:ring-primary/20"
                  : "border-gray-200 bg-white hover:border-gray-300 focus:ring-primary/20"
              )}
            >
              {/* Cuisine label */}
              <span className="text-sm font-medium">
                {cuisineData.label}
              </span>
              
              {/* Selection indicator */}
              {isCuisineSelected(key as keyof typeof CUISINE_TYPES) && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary/100 rounded-full flex items-center justify-center text-white text-xs font-bold"
                >
                  ✓
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Regional/SubType selection for selected cuisines */}
        {value.length > 0 && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700">Cuisine Details</h4>
            
            {value.map((tag) => {
              const cuisineData = CUISINE_TYPES[tag.type];
              return (
                <motion.div
                  key={tag.type}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="p-3 bg-gray-50 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary00">
                      {cuisineData.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCuisineToggle(tag.type)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Regional selection */}
                  {('regions' in cuisineData) && cuisineData.regions && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-gray-600">Region (Optional)</span>
                      <div className="flex flex-wrap gap-2">
                        {cuisineData.regions.map((region: string) => (
                          <button
                            key={region}
                            type="button"
                            onClick={() => handleRegionSelect(tag.type, region)}
                            onFocus={onFieldFocus}
                            className={cn(
                              "px-2 py-1 text-xs rounded-full border transition-colors",
                              tag.region === region
                                ? "bg-primary/10 border-primary text-primary"
                                : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                            )}
                          >
                            {region.charAt(0).toUpperCase() + region.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SubType selection */}
                  {('subTypes' in cuisineData) && cuisineData.subTypes && (
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-gray-600">Style (Optional)</span>
                      <div className="flex flex-wrap gap-2">
                        {cuisineData.subTypes.map((subType: string) => (
                          <button
                            key={subType}
                            type="button"
                            onClick={() => handleSubTypeSelect(tag.type, subType)}
                            onFocus={onFieldFocus}
                            className={cn(
                              "px-2 py-1 text-xs rounded-full border transition-colors",
                              tag.subType === subType
                                ? "bg-primary/10 border-primary text-primary"
                                : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                            )}
                          >
                            {subType.split('-').map((word: string) => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Selected cuisine tags preview */}
        {value.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-primary/10 rounded-lg p-3 border border-primary/20"
          >
            <p className="text-sm font-medium text-primary mb-2">Selected Cuisines:</p>
            <div className="flex flex-wrap gap-2">
              {value.map((tag) => {
                const cuisineData = CUISINE_TYPES[tag.type];
                const displayText = [
                  cuisineData.label,
                  tag.region && tag.region.charAt(0).toUpperCase() + tag.region.slice(1),
                  tag.subType && tag.subType.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')
                ].filter(Boolean).join(' - ');
                
                return (
                  <span
                    key={`${tag.type}-${tag.region}-${tag.subType}`}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-primary/100 text-white"
                  >
                    {displayText}
                  </span>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};